// /opt/ptg01-v1/front-simulation/components/AdminAuthGuard.tsx
// V2 — cookie-first : auth validée via GET /api/me (cookie httpOnly), pas via localStorage token.
// Le `user` metadata reste en localStorage pour UX (rendu instantané).
'use client';

import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { Loader2 } from 'lucide-react';
import { fetchAdmin } from '@/lib/fetchAdmin';

type AuthState = 'checking' | 'ready' | 'redirecting';

export default function AdminAuthGuard({ children }: { children: React.ReactNode }) {
    const router = useRouter();
    const pathname = usePathname();
    const [state, setState] = useState<AuthState>('checking');

    // Validation initiale : cookie httpOnly envoyé auto via credentials: 'include'
    useEffect(() => {
        let cancelled = false;

        async function validate() {
            try {
                const res = await fetchAdmin('/api/me');
                if (cancelled) return;

                if (res.status === 401 || res.status === 403) {
                    localStorage.removeItem('user');
                    localStorage.removeItem('loginTime');
                    router.push('/auth/login');
                    setState('redirecting');
                    return;
                }

                const json = await res.json();
                const user = json.data?.user || json.data;

                if (!json.success || !user) {
                    router.push('/auth/login');
                    setState('redirecting');
                    return;
                }

                if (user.table !== 'admin') { // aligné sur front-admin : seul un compte de la table admins entre (jamais un rôle de compte consultant)
                    localStorage.removeItem('user');
                    router.push('/auth/login');
                    setState('redirecting');
                    return;
                }

                if (user.mustChangePassword && pathname !== '/auth/change-password') {
                    router.push('/auth/change-password');
                    setState('redirecting');
                    return;
                }

                if (String(user.status || '').endsWith('_SUSPENDED')) {
                    localStorage.removeItem('user');
                    localStorage.removeItem('loginTime');
                    router.push('/auth/login?reason=session');
                    setState('redirecting');
                    return;
                }

                // Persist minimal metadata UX
                try { localStorage.setItem('user', JSON.stringify(user)); } catch {}

                // S'assure que le cookie CSRF existe (session pré-CSRF ou cookie expiré)
                if (!document.cookie.match(/(?:^|;\s*)nx_csrf=/)) {
                    fetchAdmin('/api/auth/csrf-token').catch(() => {});
                }

                setState('ready');
            } catch {
                if (!cancelled) {
                    router.push('/auth/login');
                    setState('redirecting');
                }
            }
        }

        validate();
        return () => { cancelled = true; };
    }, [router, pathname]);

    // Poll session validity every 5s
    useEffect(() => {
        if (state !== 'ready') return;
        const check = async () => {
            try {
                const res = await fetchAdmin('/api/me');
                if (res.status === 401) {
                    localStorage.removeItem('user');
                    localStorage.removeItem('loginTime');
                    // Rechargement complet volontaire : session perdue, on repart d'un état vierge.
            window.location.assign(new URL('/auth/login?reason=session', window.location.origin));
                }
            } catch {}
        };
        const interval = setInterval(check, 5000);
        return () => clearInterval(interval);
    }, [state]);

    if (state !== 'ready') return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
            <Loader2 className="w-6 h-6 text-[#000091] animate-spin" />
        </div>
    );

    return <>{children}</>;
}
